import { useMemo, useState, type RefObject } from 'react'
import { Link } from 'react-router'
import { useQueries } from '@tanstack/react-query'
import { t } from '../../i18n'
import { Button, Field, Icon, Input, Note, OptionList, Tag, type Option } from '../../ui'
import { POLL_MS, RELAY, getAsOf, nodePath, relayKeys, routePath, type RelayList, type RelayRoute } from './api'
import { centerNow } from './clock'
import { GroupDialog } from './GroupDialog'
import { hubUnfit, rehomeErrorText, storedState, groupsApi, type GroupMemberView } from './groups'
import { noteRelayAsOf, noteRelayFailure } from './live'
import { tn } from './nodeSwitch'

/*
 * GROUP-UI-2: the broadcasts of a node group and moving their outputs (GROUP-3 rehome,
 * node-group.md §6.4). A broadcast's outputs leave from one outputs node — a member of
 * the group (egress_offload) or a node of the center. Moving them is ConfirmDialog
 * level 3 with the object's name typed (the route, mono) and the code: a platform
 * session cannot move between machines, so every platform reconnects and on Twitch
 * it is a new stream. The dialog says so as a warn note before anything else; only
 * its action button is danger (the row's button that opens it is plain). Refusals are
 * words by the API's code, with "you can try in N min" from retry_after_s.
 *
 * DS: NG-09 (decided 01.11) — "outputs node" (the API's hub role never shows), the
 * route id typed, the title names the route and the target; until the system import
 * carries the glossary line.
 */

/** A live route whose ingress is a member of the group. */
export interface GroupRoute {
  route_id: string
  stream: string
  ingress: { id: string; name: string }
  egress: { id: string; name: string }
  /** the outputs node is a member of the group (offload), else a node of the center */
  memberHub: boolean
  outputs: number
  since?: string
}

/** The live routes of the members (GET /relay/routes?node=, the same queries the node card polls). */
export function useGroupRoutes(members: GroupMemberView[], enabled: boolean) {
  const ids = members.map((m) => m.node)
  const qs = useQueries({
    queries: ids.map((id) => ({
      queryKey: relayKeys.nodeRoutes(id),
      enabled,
      queryFn: async () => {
        try {
          const r = await getAsOf<RelayList<RelayRoute>>(`${RELAY}/routes?node=${encodeURIComponent(id)}&limit=500`)
          noteRelayAsOf(r.asOf, r.at)
          return r
        } catch (e) {
          noteRelayFailure(e)
          throw e
        }
      },
      refetchInterval: POLL_MS,
      retry: false,
    })),
  })
  const key = qs.map((q) => q.dataUpdatedAt).join('|') + ids.join(',')
  const routes = useMemo(() => {
    const set = new Set(ids)
    const by = new Map<string, GroupRoute>()
    for (const q of qs) {
      for (const r of q.data?.data.items ?? []) {
        if (r.state === 'ended' || by.has(r.route_id) || r.path.length === 0) continue
        const inn = r.path[0]!
        const out = r.path[r.path.length - 1]!
        if (!set.has(inn.id)) continue
        by.set(r.route_id, {
          route_id: r.route_id, stream: r.stream, ingress: inn, egress: out, memberHub: set.has(out.id) && out.id !== inn.id,
          outputs: r.outputs?.total ?? 0, since: r.source?.since ?? r.created_at,
        })
      }
    }
    return [...by.values()].sort((a, b) => a.stream.localeCompare(b.stream))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  const error = qs.find((q) => q.error)?.error
  return { routes, loading: enabled && qs.some((q) => q.isPending), error: routes.length ? undefined : error }
}

/** "1 h 58 min", "12 min" in the language (numbers mono where shown). */
function age(since?: string): string {
  if (!since) return '—'
  const m = Math.max(0, Math.floor((centerNow() - Date.parse(since)) / 60000))
  const h = Math.floor(m / 60)
  return h ? t('group.bc.ageH', { h, m: m % 60 }) : t('group.bc.ageM', { m })
}

export function GroupBroadcasts({ routes, loading, error, open }: { routes: GroupRoute[]; loading: boolean; error?: unknown; open: (r: GroupRoute, el: HTMLElement) => void }) {
  return (
    <section className="sx-panel flex flex-col gap-0 !p-0" aria-labelledby="group-bc-title" data-testid="group-broadcasts">
      <div className="sx-toolbar">
        <span className="sx-label" id="group-bc-title">{t('group.bc.title')}</span>
        <span className="sx-mono text-muted">{routes.length}</span>
      </div>
      {error ? (
        <div className="p-3"><Note state="warn" data-testid="group-bc-error">{t('group.bc.error')}</Note></div>
      ) : routes.length === 0 ? (
        <p className="px-3 py-3 text-[13px] text-muted" data-testid="group-bc-empty">{loading ? '…' : t('group.bc.empty')}</p>
      ) : (
        <>
          <div className="hidden overflow-x-auto md:block">
            <table className="sx-table compact" data-testid="group-bc-table">
              <thead>
                <tr>
                  <th scope="col">{t('group.bc.col.stream')}</th>
                  <th scope="col">{t('group.bc.col.in')}</th>
                  <th scope="col">{t('group.bc.col.from')}</th>
                  <th scope="col" className="num">{t('group.bc.col.outputs')}</th>
                  <th scope="col" className="num">{t('group.bc.col.age')}</th>
                  <th scope="col" className="num"><span className="sr-only">{t('group.col.actions')}</span></th>
                </tr>
              </thead>
              <tbody>
                {routes.map((r) => (
                  <tr key={r.route_id} data-testid={`group-bc-${r.route_id}`}>
                    <td>
                      <Link to={routePath(r.route_id)} className="sx-mono hover:underline">{r.stream}</Link>
                    </td>
                    <td><Link to={nodePath(r.ingress.id)} className="sx-mono hover:underline">{r.ingress.name}</Link></td>
                    <td><From r={r} /></td>
                    <td className="num">{r.outputs}</td>
                    <td className="num">{age(r.since)}</td>
                    <td className="num">
                      <Button size="sm" data-testid={`group-move-${r.route_id}`} onClick={(e) => open(r, e.currentTarget)}>{t('group.btn.move')}</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="flex flex-col md:hidden" data-testid="group-bc-cards">
            {routes.map((r) => (
              <li key={r.route_id} className="flex flex-col gap-2 border-t border-line px-3 py-3" data-testid={`group-bc-card-${r.route_id}`}>
                <span className="flex items-center justify-between gap-2">
                  <Link to={routePath(r.route_id)} className="sx-mono inline-flex min-h-[44px] items-center font-medium">{r.stream}</Link>
                  <span className="sx-mono text-muted">{age(r.since)}</span>
                </span>
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
                  <span className="sx-mono">{r.ingress.name}</span>
                  <Icon name="chevron-right" size={12} className="text-muted" />
                  <From r={r} />
                  <span className="sx-mono text-muted">{tn('group.bc.outputsN', { n: r.outputs })}</span>
                </span>
                <Button size="sm" className="min-h-[44px]" data-testid={`group-move-${r.route_id}`} onClick={(e) => open(r, e.currentTarget)}>{t('group.btn.move')}</Button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

/** Where the outputs leave from: a member (OUTPUTS tag is the member's), else a center node with CENTER. */
function From({ r }: { r: GroupRoute }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap" data-testid={`group-bc-from-${r.route_id}`} data-member={r.memberHub ? 'true' : undefined}>
      <Link to={nodePath(r.egress.id)} className="sx-mono hover:underline">{r.egress.name}</Link>
      {!r.memberHub && <Tag>{t('group.bc.center')}</Tag>}
    </span>
  )
}

/** Move the outputs of one broadcast: where to (members with their reasons, or the center), the warning, the route typed, the code. */
export function MoveDialog({ r, members, group, trigger, onClose, onDone }: { r: GroupRoute; members: GroupMemberView[]; group: string; trigger: RefObject<HTMLElement | null>; onClose: () => void; onDone: (text: string) => Promise<void> }) {
  const options: Option<string>[] = [
    ...members.map((m) => ({
      value: m.node,
      label: <span className="sx-mono">{m.name}</span>,
      tags: m.hub_routes ? <Tag>{t('group.hub.tag', { n: m.hub_routes })}</Tag> : undefined,
      right: storedState(m) === 'serving' ? <span className="sx-mono">{`${Math.round(m.load.load * 100)} %`}</span> : undefined,
      why: hubUnfit(m, { ingress: r.ingress.id, egress: r.egress.id }),
      testId: `move-to-${m.node}`,
    })),
    { value: 'center', label: <span>{t('group.move.center')} <span className="text-muted">· {t('group.move.centerPick')}</span></span>, why: r.memberHub ? undefined : t('group.move.why.centerNow'), testId: 'move-to-center' },
  ]
  const first = options.find((o) => o.why === undefined)?.value ?? ''
  const [to, setTo] = useState(first)
  const [typed, setTyped] = useState('')
  const toName = to === 'center' ? t('group.move.center') : (members.find((m) => m.node === to)?.name ?? to)
  const title = to === 'center' ? tn('group.confirm.moveAuto', { route: r.route_id }) : tn('group.confirm.move', { route: r.route_id, to: toName })
  const word = r.route_id
  const ready = !!to && typed.trim() === word
  return (
    <GroupDialog
      testId="group-move"
      title={title}
      danger
      busyLabel={t('group.move.busy')}
      action={t('group.btn.move')}
      ready={ready}
      returnFocus={trigger}
      onClose={onClose}
      errorText={(e) => rehomeErrorText(e, { to: toName, group })}
      submit={async (totp) => {
        const res = await groupsApi.rehome(r.route_id, to, totp)
        const name = members.find((m) => m.node === res.to)?.name ?? res.to
        await onDone(t('group.done.move', { stream: r.stream, to: name }))
      }}
    >
      <Note state="warn" data-testid="group-move-warn">{t('group.move.warn')}</Note>
      <p className="text-[13px] text-text-soft" data-testid="group-move-from">{tn('group.move.from', { stream: r.stream, from: r.egress.name, n: r.outputs })}</p>
      <div className="sx-field">
        <span className="lbl" id="group-move-to-label">{t('group.move.to')}</span>
        <OptionList value={to} options={options} onChange={setTo} labelledBy="group-move-to-label" />
      </div>
      <Field id="group-move-word" label={tn('group.move.type', { route: word })}>
        <Input id="group-move-word" value={typed} spellCheck={false} autoComplete="off" onChange={(e) => setTyped(e.target.value)} data-testid="group-move-word" />
      </Field>
    </GroupDialog>
  )
}
